import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { produce } from 'immer';
import {
  BehaviorSubject,
  concatMap,
  defer,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { runtimeActions } from '@blaze-runtime/engine-data-access';
import { CanvasElementId, CanvasElementType, getNamespaceId } from '@blaze-runtime/engine-domain';
import {
  CanvasElementDataProperties,
  CanvasElementInputBinding,
  DataBindingSourceInfo,
  DataSourceSelectMode,
  getActiveBinding,
  getNewApiCallSourceNode,
  getNewGetSourceNode,
  mergeDataBindingsValues,
} from '@blaze-shared/elements-domain';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetSchema } from '@blaze-shared/schema-domain';
import {
  Connection,
  findFirstNodeInWorkflowOfType,
  isGetNode,
  Workflow,
  WorkflowNode,
} from '@blaze-shared/workflow-domain';

import {
  elementDataSourceSelected,
  refreshBinding,
  updateElementProps,
} from '../+state/elements/elements.actions';
import { RuntimeFacade } from '../+state/runtime/runtime.facade';
import {
  createElementDataPatch,
  createElementDataSourceBinding,
} from '../canvas-elements/element-model/data-binding';
import { getUiRuntimeElement } from '../utils/runtime/runtime';

import { ElementInputWorkflowService } from './workflow-generation/public-api';
import { WorkflowUtilsService } from './workflow-utils.service';

/**
 * Internal data source id used to store various ids and match it with the loaded options.
 *
 * For datasets and workflows it directly stores a dataset or workflow id (they're uuids, so they won't collide).
 * For API Calls the format is different see {@link getApiCallSourceId}
 */
export type DataSourceId = string;

export interface SetDataSourceResult {
  workflow?: Workflow;
  /** null means dynamic search not supported */
  applyDynamicSearch?: boolean | null;
}

export interface WorkflowGeneratedData {
  elementId: CanvasElementId;
  localItemModel: CanvasElementDataProperties[];
  dataSourceId: string;
  dataSource: DataBindingSourceInfo | undefined;
}

export type GenerateWorkflowData = {
  /** Opens workflow configuration dialog after generating the workflow. */
  openDialog?: boolean;
  bindings?: Record<string, string>;
} & GenerateWorkflowSourceData;

export type GenerateWorkflowSourceData =
  | { type?: undefined }
  | { type: 'dataset'; datasetId: string; projectColumns?: string[] }
  | { type: 'api-call'; connectorId?: string; actionName?: string };

export interface UpdateKnobPropsParams {
  isCustomSelection?: boolean;
  localItemModel?: CanvasElementDataProperties[];
  dataSourceId?: string;
}
type UpdateKnobPropsFn = (
  elementId: CanvasElementId | undefined,
  params: UpdateKnobPropsParams,
) => void;

export const NEW_CUSTOM_WORKFLOW = 'NEW_CUSTOM_WORKFLOW';
export const NEW_API_CALL = 'NEW_API_CALL';
export const BIG_TABLE_THRESHOLD = 1000;

@Injectable({ providedIn: 'root' })
export class ElementDataSourceService {
  private readonly actions$ = inject(Actions);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly elementInputWorkflowService = inject(ElementInputWorkflowService);
  private readonly workflowUtilsService = inject(WorkflowUtilsService);

  private readonly bindingsUpdated$$ = new BehaviorSubject<
    | {
        elementId: CanvasElementId;
        data: CanvasElementDataProperties[];
        workflow: Workflow | undefined;
      }
    | undefined
  >(undefined);
  private readonly workflowGenerated$$ = new BehaviorSubject<WorkflowGeneratedData | undefined>(
    undefined,
  );

  bindingsUpdated$ = this.bindingsUpdated$$.pipe(filter((value) => value !== undefined));
  workflowGenerated$ = this.workflowGenerated$$.pipe(filter((value) => value !== undefined));

  /**
   * Handles the API Call option selection.
   *
   * When API Call option is selected, the an empty API Call binding is created to preserve the source selection,
   * but it doesn't do until it gets configured in a configurator dialog which popups when a new API Call is created.
   * After the API Call gets configured and saved inside of the dialog, the input workflow gets created or updated.
   *
   * When the API Call is not selected, selecting it will always be a new API Call configuration.
   * Previously configured API Call configurations are not stored anywhere after they get deselected.
   * Clicking on an already created API Call won't trigger any updates.
   */
  setDataSourceToApiCall({
    elementId,
    elementType,
    bindingKey,
    dataProps,
    dataSource,
    previousDataSourceId,
    selectionEvent,
    rawOptionsWorkflows,
    apiConfigStoreAction,
    updateKnobPropsFn,
  }: {
    elementId: CanvasElementId | undefined;
    elementType: CanvasElementType | undefined;
    bindingKey: CanvasElementInputBinding | undefined;
    dataProps: CanvasElementDataProperties[];
    dataSource: DataBindingSourceInfo;
    previousDataSourceId: string | undefined;
    selectionEvent: Event;
    rawOptionsWorkflows: DataBindingSourceInfo[];
    apiConfigStoreAction: Action;
    updateKnobPropsFn: UpdateKnobPropsFn;
  }): Observable<SetDataSourceResult> {
    // Defer so the code gets executed lazily on subscription
    return defer(() => {
      const elementDataSource = getElementDataSource(bindingKey, dataProps);

      // Find workflow to update
      const workflowId = this.getInputWorkflow(
        elementDataSource,
        bindingKey,
        dataProps,
        rawOptionsWorkflows,
      );

      // Confirm if Custom workflow is about to be overwritten
      if (workflowId && !this.confirmOverwrite(workflowId)) {
        selectionEvent.preventDefault(); // If user cancels - don't update the selection
        return EMPTY;
      }

      // Update the selection - the connector and API action is not chosen yet
      updateKnobPropsFn(elementId, {
        dataSourceId: getApiCallSourceId(dataSource.id, dataSource.name),
        isCustomSelection: false,
      });

      // New API Call (not yet configured) - create binding and open the API Call configurator
      if (dataSource.id === NEW_API_CALL) {
        if (!bindingKey) {
          throw new Error('Binding key missing');
        }

        const newBinding: CanvasElementDataProperties = {
          key: bindingKey,
          dataSource: getNewApiCall(),
          bindings: {},
          enabled: true,
          isCustomSelection: false,
        };
        dataProps = mergeDataBindingsValues(dataProps, [newBinding]);
        updateKnobPropsFn(elementId, { localItemModel: dataProps });
        this.updateBindingSchema({
          dataProps,
          workflow: undefined,
          elementId,
          bindingKey,
          isNewDataSource: true,
          isCustomSelection: false,
        });
        this.openApiCallConfigDialog(elementId, apiConfigStoreAction);

        return of({ workflow: undefined }); // Emit a value and exit the code here
      } else {
        // An API Call option was selected - it's the same as currently configured by design,
        // because there's only one configurable API Call option in the list, so no action should be taken
        return EMPTY;
      }
    }).pipe(
      this.elementDataSourceSelected(elementId, elementType, previousDataSourceId, dataSource),
    );
  }

  setDataSourceToDataset({
    bindingKey,
    dataProps,
    selectedDataset,
    isNewDataSource,
    selectionEvent,
    mode,
    elementId,
    elementType,
    projectId,
    previousDataSourceId,
    setDynamicSearchIfBigTable,
    rawOptionsWorkflows,
    updateKnobPropsFn,
  }: {
    bindingKey: CanvasElementInputBinding | undefined;
    dataProps: CanvasElementDataProperties[];
    selectedDataset: DataBindingSourceInfo;
    isNewDataSource: boolean;
    selectionEvent?: Event;
    mode: DataSourceSelectMode | undefined;
    elementId: CanvasElementId | undefined;
    projectId: string;
    elementType: CanvasElementType | undefined;
    previousDataSourceId: string | undefined;
    setDynamicSearchIfBigTable: boolean;
    rawOptionsWorkflows: DataBindingSourceInfo[];
    updateKnobPropsFn: UpdateKnobPropsFn;
  }): Observable<SetDataSourceResult> {
    // Defer so the code gets executed lazily on subscription
    return defer(() => {
      if (!bindingKey) {
        throw new Error('Binding key missing');
      }
      const elementDataSource = getElementDataSource(bindingKey, dataProps);

      // Find workflow to update
      const workflowId = this.getInputWorkflow(
        elementDataSource,
        bindingKey,
        dataProps,
        rawOptionsWorkflows,
      );

      // Check row count to use dynamic search if available and table has many rows
      let applyDynamicSearch$: Observable<boolean | null | undefined> = of(undefined);
      if (setDynamicSearchIfBigTable) {
        applyDynamicSearch$ = this.datasetRowsService.count(selectedDataset.id).pipe(
          map((count) => {
            if (count === null) {
              return null;
            } // Dynamic search unsupported
            else if (count > BIG_TABLE_THRESHOLD) {
              return true;
            } // Apply dynamic search for big tables
            else {
              return undefined;
            } // Otherwise don't change
          }),
        );
      }

      // If no input workflow exists - create a new one from scratch
      if (!workflowId) {
        return applyDynamicSearch$.pipe(
          concatMap((applyDynamicSearch) =>
            this.generateWorkflow({
              generationData: { type: 'dataset', datasetId: selectedDataset.id },
              bindingKey,
              dataProps,
              elementId,
              projectId,
              isCustomSelection: false,
              // Limit results if action mode or dynamic search should be applied
              // For the dynamic search - the max results will be adjusted later on - it's limited here so the result doesn't hang the browser
              limitResults: mode === 'form-action' || mode === 'data-filter',
              mode,
            }).pipe(map(({ workflow }) => ({ workflow, applyDynamicSearch }))),
          ),
        );
      }

      // Confirm if Custom workflow is about to be overwritten
      if (!this.confirmOverwrite(workflowId)) {
        selectionEvent?.preventDefault(); // If user cancels - don't update the selection
        return EMPTY;
      }

      // If binding exists and doesn't point to a workflow - update it so it uses the workflow
      const binding = dataProps.find((b) => b.key === bindingKey);
      if (
        binding?.dataSource?.sourceType !== 'context' &&
        binding?.dataSource?.sourceType !== 'data-filter'
      ) {
        const workflowBinding = createElementDataSourceBinding(
          bindingKey,
          workflowId,
          selectedDataset.name,
          selectedDataset.columns,
          false,
        );

        // Update the binding
        dataProps = mergeDataBindingsValues(dataProps, [workflowBinding]);
        updateKnobPropsFn(elementId, { localItemModel: dataProps });
      }

      // Update the selected data source
      const dataSourceId = getDataSourceId(selectedDataset);
      updateKnobPropsFn(elementId, { dataSourceId, isCustomSelection: false });

      const resetWorkflow =
        elementDataSource?.sourceType === 'context' && this.isCustomWorkflow(elementDataSource.id);

      // Update the workflow
      return applyDynamicSearch$.pipe(
        map((applyDynamicSearch) => {
          return {
            applyDynamicSearch,
            sourceNode: getNewGetSourceNode(
              selectedDataset.id,
              mode === 'form-action' || applyDynamicSearch === true,
            ),
          };
        }),
        concatMap(({ applyDynamicSearch, sourceNode }) =>
          this.switchWorkflowSourceNode({
            workflowId,
            datasetId: dataSourceId,
            bindingKey,
            elementId,
            dataProps,
            sourceNode,
            isNewDataSource,
            isCustomSelection: false,
            resetToSimpleGet: resetWorkflow,
          }).pipe(map((workflow) => ({ workflow, applyDynamicSearch }))),
        ),
      );
    }).pipe(
      this.elementDataSourceSelected(elementId, elementType, previousDataSourceId, selectedDataset),
    );
  }

  setDataSourceToWorkflow({
    bindingKey,
    dataProps,
    dataSource,
    isNewDataSource,
    previousDatasetId,
    mode,
    elementId,
    elementType,
    projectId,
    rawOptionsDatasets,
    updateKnobPropsFn,
  }: {
    bindingKey: CanvasElementInputBinding | undefined;
    dataProps: CanvasElementDataProperties[];
    dataSource: DataBindingSourceInfo;
    isNewDataSource: boolean;
    previousDatasetId: string | undefined;
    mode: DataSourceSelectMode | undefined;
    elementId: CanvasElementId | undefined;
    projectId: string;
    elementType: CanvasElementType | undefined;
    rawOptionsDatasets: DataBindingSourceInfo[];
    updateKnobPropsFn: UpdateKnobPropsFn;
    isAiBuilderFlow?: boolean;
  }): Observable<SetDataSourceResult> {
    // Defer so the code gets executed lazily on subscription
    return defer(() => {
      if (dataSource.id === NEW_CUSTOM_WORKFLOW) {
        // Select new custom workflow (scenario where no previous workflow exists)
        updateKnobPropsFn(elementId, { isCustomSelection: true });
        const generationData: GenerateWorkflowData = { openDialog: true };
        if (previousDatasetId && rawOptionsDatasets.find(({ id }) => id === previousDatasetId)) {
          Object.assign(generationData, {
            type: 'dataset',
            datasetId: previousDatasetId,
          });
        }
        return this.generateWorkflow({
          generationData,
          bindingKey,
          elementId,
          projectId,
          dataProps,
          isCustomSelection: true,
          limitResults: mode === 'form-action',
          mode,
        });
      }

      if (!bindingKey) {
        throw new Error('Binding key missing');
      }

      const workflowId = dataSource.id;
      const workflow = this.runtimeFacade.getWorkflowById(workflowId);
      if (!workflow) {
        throw new Error(`Workflow ${workflowId} missing`);
      }

      // Find existing binding or create a new one
      const binding = dataProps.find((b) => b.key === bindingKey) || {
        key: bindingKey,
        bindings: {},
        dataSource,
        enabled: true,
      };

      // Update the data source
      dataProps = mergeDataBindingsValues(dataProps, [{ ...binding, dataSource }]);
      updateKnobPropsFn(elementId, {
        isCustomSelection: true,
        dataSourceId: workflowId,
        localItemModel: dataProps,
      });

      this.updateBindingSchema({
        workflow,
        isNewDataSource,
        bindingKey,
        dataProps,
        isCustomSelection: true,
        elementId,
      });

      return this.openWorkflowConfigDialog({
        bindingKey,
        dataProps,
        elementId,
        isCustomSelection: true,
        workflowId: workflow.id,
        isNewDataSource: false,
        workflow,
      }).pipe(map((workflow) => ({ workflow })));
    }).pipe(this.elementDataSourceSelected(elementId, elementType, previousDatasetId, dataSource));
  }

  /**
   * Converts the data source to a workflow if it's not a workflow
   * @returns workflowId
   */
  ensureWorkflowSource({
    datasetId,
    dataSource,
    bindingKey,
    elementId,
    projectId,
    dataProps,
    isCustomSelection,
    limitResults,
    mode,
    projectColumns,
  }: {
    datasetId: string | undefined;
    dataSource: DataBindingSourceInfo | undefined;
    bindingKey: CanvasElementInputBinding | undefined;
    elementId: string | undefined;
    projectId: string;
    dataProps: CanvasElementDataProperties[];
    isCustomSelection: boolean;
    limitResults: boolean;
    mode?: DataSourceSelectMode;
    projectColumns?: string[];
  }): Observable<string> {
    if (!mode) {
      mode = 'normal';
    }
    if (!elementId || !bindingKey) {
      throw new Error('Data missing');
    }
    if (!dataSource?.id) {
      throw new Error('Incorrect data source');
    }

    // Look if input workflow exists
    const binding = getActiveBinding(dataProps, bindingKey);
    const inputWorkflow = this.runtimeFacade.getWorkflowsByTarget(
      getUiRuntimeElement(elementId),
    )?.[0];

    if (binding?.dataSource?.sourceType === 'context') {
      // if binding source is currently set to a workflow - keep this selection
      return of(dataSource.id);
    } else if (inputWorkflow !== undefined) {
      // if not bound to a workflow and input workflow already present - use it
      return of(inputWorkflow.id).pipe(
        tap((workflowId) => {
          const dataSourceName = binding?.dataSource?.name ?? '';
          const newBinding = createElementDataSourceBinding(
            bindingKey,
            workflowId,
            dataSourceName,
            [],
            false,
            binding?.bindings,
          );
          dataProps = mergeDataBindingsValues(dataProps, [newBinding]);
        }),
        concatMap((workflowId) =>
          this.switchWorkflowSourceNode({
            workflowId,
            datasetId,
            bindingKey,
            elementId,
            dataProps,
            sourceNode: getNewGetSourceNode(dataSource.id, limitResults),
            isNewDataSource: false,
            isCustomSelection,
            resetToSimpleGet: true,
          }),
        ),
        map((workflow) => workflow.id),
      );
    } else {
      // otherwise - generate a new input workflow
      return this.generateWorkflow({
        generationData: {
          type: 'dataset',
          datasetId: dataSource.id,
          bindings: binding?.bindings,
          projectColumns,
        },
        bindingKey,
        elementId,
        projectId,
        dataProps,
        isCustomSelection,
        limitResults,
        mode,
      }).pipe(map(({ workflow }) => workflow.id));
    }
  }

  /**
   *
   * @param workflowId
   * @param sourceNode the source node (for example 'Get' or 'ApiCall' node) which contains
   * @param isNewDataSource
   * @param resetToSimpleGet
   * @returns
   */
  switchWorkflowSourceNode({
    workflowId,
    datasetId,
    bindingKey,
    elementId,
    dataProps,
    sourceNode,
    isNewDataSource,
    isCustomSelection,
    resetToSimpleGet,
  }: {
    workflowId: string;
    datasetId: string | undefined;
    bindingKey: CanvasElementInputBinding | undefined;
    elementId: string | undefined;
    dataProps: CanvasElementDataProperties[];
    sourceNode: WorkflowNode;
    isNewDataSource: boolean;
    isCustomSelection: boolean;
    resetToSimpleGet?: boolean;
  }): Observable<Workflow> {
    if (!workflowId || !datasetId || !elementId) {
      return EMPTY;
    }

    return this.schemaService.obtainSchema<DatasetSchema>(datasetId).pipe(
      switchMap(() =>
        this.elementInputWorkflowService.addOrUpdateWorkflowSourceNode(
          workflowId,
          sourceNode,
          resetToSimpleGet,
        ),
      ),
      tap((workflow) => {
        this.updateBindingSchema({
          workflow,
          bindingKey,
          elementId,
          dataProps,
          isNewDataSource,
          isCustomSelection,
        });
      }),
    );
  }

  generateWorkflow({
    generationData,
    bindingKey,
    elementId,
    projectId,
    dataProps,
    isCustomSelection,
    limitResults,
    mode,
  }: {
    generationData: GenerateWorkflowData;
    bindingKey: CanvasElementInputBinding | undefined;
    elementId: string | undefined;
    projectId: string | undefined;
    dataProps: CanvasElementDataProperties[];
    isCustomSelection: boolean;
    limitResults: boolean;
    mode: DataSourceSelectMode | undefined;
  }): Observable<{ workflow: Workflow; connection: Connection }> {
    const openConfigDialog = generationData?.openDialog || false;
    if (!bindingKey || !elementId || !projectId) {
      return EMPTY;
    }

    let sourceNode: WorkflowNode | undefined;

    // Trigger schema loading
    let obtainSchema$: Observable<DatasetSchema | undefined> = of(undefined);
    if (generationData?.type === 'dataset') {
      sourceNode = getNewGetSourceNode(
        generationData.datasetId,
        limitResults,
        generationData.projectColumns,
      );
      obtainSchema$ = this.schemaService.obtainSchema<DatasetSchema>(generationData.datasetId);
    } else if (generationData?.type === 'api-call') {
      sourceNode = getNewApiCallSourceNode(generationData.connectorId, generationData.actionName);
    }

    let dataSourceName: string | undefined;
    return obtainSchema$.pipe(
      tap((schema) => {
        if (schema) {
          dataSourceName = schema.name;
        }
      }),
      switchMap(() => {
        const ns = getNamespaceId(elementId);
        return this.elementInputWorkflowService.generateElementInputWorkflow(
          projectId,
          elementId,
          ns,
          sourceNode,
        );
      }),
      switchMap(({ workflow, connection }) => {
        if (mode !== 'data-filter') {
          this.store.dispatch(runtimeActions.connectionAdd({ connection }));
        }
        this.store.dispatch(runtimeActions.workflowAdd({ workflow }));

        const elementPropsPatch = createElementDataPatch(
          elementId,
          bindingKey,
          workflow.id,
          [],
          isCustomSelection,
          generationData?.bindings,
          dataSourceName,
        );
        if (mode === 'data-filter' && elementPropsPatch.value.dataSource?.sourceType) {
          elementPropsPatch.value.dataSource.sourceType = 'data-filter';
        }
        this.store.dispatch(updateElementProps(elementPropsPatch));

        dataProps = mergeDataBindingsValues(dataProps, [elementPropsPatch.value]);

        // Update the data source in stored options
        const dataSource = elementPropsPatch.value.dataSource;
        this.workflowGenerated$$.next({
          elementId,
          localItemModel: dataProps,
          dataSourceId: workflow.id,
          dataSource,
        });

        if (openConfigDialog) {
          return this.openWorkflowConfigDialog({
            bindingKey,
            dataProps,
            isCustomSelection,
            isNewDataSource: true,
            workflowId: workflow.id,
            elementId,
            updateBindingSchema: true,
            workflow,
          }).pipe(map((wf) => ({ workflow: wf || workflow, connection })));
        } else if (generationData?.type === 'dataset' || generationData?.type === 'api-call') {
          this.updateBindingSchema({
            workflow,
            bindingKey,
            elementId,
            dataProps,
            isNewDataSource: true,
            isCustomSelection,
          });
        }

        return of({ workflow, connection });
      }),
    );
  }

  openWorkflowConfigDialog({
    bindingKey,
    dataProps,
    elementId,
    workflowId,
    isCustomSelection,
    isNewDataSource,
    workflow,
    showOldDialog,
    updateBindingSchema,
  }: {
    bindingKey: CanvasElementInputBinding | undefined;
    dataProps: CanvasElementDataProperties[];
    isCustomSelection: boolean;
    isNewDataSource: boolean;
    workflowId: string;
    elementId: string | undefined;
    showOldDialog?: boolean;
    updateBindingSchema?: boolean;
    workflow?: Workflow;
  }): Observable<Workflow | undefined> {
    return defer(() => {
      const ns = workflow?.pageId || (elementId && getNamespaceId(elementId));
      this.store.dispatch(
        runtimeActions.editElementWorkflow({ workflowId, elementId, showOldDialog, ns }),
      );

      return this.actions$.pipe(
        ofType(runtimeActions.workflowEditDialogClosed),
        filter((a) => a.workflowId === workflowId),
        take(1),
        map((result) => result.workflow || workflow),
        tap((wf) => {
          if (wf && updateBindingSchema) {
            this.updateBindingSchema({
              bindingKey,
              dataProps,
              isCustomSelection,
              workflow: wf,
              elementId,
              isNewDataSource,
            });
          }
        }),
      );
    });
  }

  openApiCallConfigDialog(
    elementId: CanvasElementId | undefined,
    apiConfigStoreAction: Action,
  ): void {
    if (!elementId) {
      return;
    }
    this.store.dispatch(apiConfigStoreAction);
  }

  updateBindingSchema({
    workflow,
    elementId,
    bindingKey,
    dataProps,
    isNewDataSource,
    isCustomSelection,
  }: {
    dataProps: CanvasElementDataProperties[];
    isCustomSelection: boolean;
    isNewDataSource: boolean;
    bindingKey: CanvasElementInputBinding | undefined;
    elementId: string | undefined;
    workflow: Workflow | undefined;
  }): void {
    if (!elementId || !bindingKey) {
      throw new Error('Element id or binding key missing');
    }

    // Update the 'Custom workflow' selection flag
    const data = produce(dataProps, (draft) => {
      const binding = draft?.find((b) => b.key === bindingKey);
      if (binding) {
        binding.isCustomSelection = isCustomSelection;
      }
    });

    // Update the data source value and options
    this.bindingsUpdated$$.next({ elementId, data, workflow });

    // Refresh binding to update the schemas and cause side-effects (columns refresh/form regeneration/etc)
    this.store.dispatch(refreshBinding({ elementId, bindingKey, isNewDataSource }));
  }

  getInputWorkflow(
    elementDataSource: DataBindingSourceInfo | undefined,
    bindingKey: CanvasElementInputBinding | undefined,
    dataProps: CanvasElementDataProperties[],
    rawOptionsWorkflows: DataBindingSourceInfo[],
  ): string | undefined {
    if (
      !elementDataSource?.id ||
      elementDataSource.sourceType === 'dataset' ||
      elementDataSource.sourceType === 'api-call'
    ) {
      // If no previous data source or basic dataset binding (without workflow) is used -> use existing workflow if exists
      return this.getWorkflowsNotBoundToOtherInputs(bindingKey, dataProps, rawOptionsWorkflows)[0]
        ?.id;
    }

    if (
      elementDataSource.sourceType === 'context' ||
      elementDataSource.sourceType === 'data-filter'
    ) {
      // The data source is already using a workflow - update the workflow to point to the selected dataset
      return elementDataSource.id;
    }

    throw new Error(`Unexpected source option ${elementDataSource.sourceType}`);
  }

  getWorkflowsNotBoundToOtherInputs(
    bindingKey: CanvasElementInputBinding | undefined,
    dataProps: CanvasElementDataProperties[],
    rawOptionsWorkflows: DataBindingSourceInfo[],
  ): DataBindingSourceInfo[] {
    if (!dataProps.length) {
      return [];
    }

    const workflowIdsBoundToOtherInputs = dataProps
      .filter(({ key, dataSource }) => key !== bindingKey && dataSource?.sourceType === 'context')
      .map((data) => data.dataSource?.id)
      .filter(Boolean);

    return rawOptionsWorkflows.filter((w) => !workflowIdsBoundToOtherInputs.includes(w.id));
  }

  private elementDataSourceSelected(
    elementId: CanvasElementId | undefined,
    elementType: CanvasElementType | undefined,
    previousDataSourceId: string | undefined,
    selectedDataSource: DataBindingSourceInfo,
  ) {
    return (input$: Observable<any>): Observable<any> =>
      input$.pipe(
        tap(({ workflow, applyDynamicSearch }) => {
          if (!elementId) {
            throw new Error('Element id missing');
          }
          if (!elementType) {
            throw new Error('Element type missing');
          }
          const isInitialSelection = !previousDataSourceId;
          let dataSourceName: string | undefined;
          if (workflow) {
            dataSourceName = this.schemaService.getTableName(
              findFirstNodeInWorkflowOfType(workflow, isGetNode)?.datasetId,
            );
          }
          this.store.dispatch(
            elementDataSourceSelected({
              elementId: elementId,
              elementType: elementType,
              selectedDataSource,
              dataSourceName,
              isInitialSelection,
              applyDynamicSearch,
            }),
          );
        }),
      );
  }

  /** Confirms custom workflow overwrite */
  private confirmOverwrite(workflowId: string): boolean {
    const canOverwrite =
      !this.isCustomWorkflow(workflowId) ||
      confirm('This will overwrite the current Custom workflow config. Continue?');
    return canOverwrite;
  }

  private isCustomWorkflow(workflowId: string): boolean {
    const workflow = this.runtimeFacade.getWorkflowById(workflowId);
    if (!workflow) {
      throw new Error(`Workflow ${workflowId} does not exist`);
    }
    return this.workflowUtilsService.getWorkflowDisplayData(workflow).custom || false;
  }
}

/**
 * Generates the internal data source id for the API Call
 *
 * @returns a string with the API Call id
 * - if all data is provided: `api-${connectorId}-${actionName}`
 * - if action name is missing: `api-${connectorId}`
 * - if neither connector id nor action name is provided: `api`
 */
export function getApiCallSourceId(
  connectorId: string | undefined,
  actionName: string | undefined,
): string {
  const idBuilder: string[] = ['api'];

  if (connectorId && connectorId !== NEW_API_CALL) {
    idBuilder.push(connectorId);

    // Append the action name if the connector id is present only
    if (actionName) {
      idBuilder.push(actionName);
    }
  }

  return idBuilder.join('-');
}

export function getElementDataSource(
  bindingKey: CanvasElementInputBinding | undefined,
  data?: CanvasElementDataProperties[],
): DataBindingSourceInfo | undefined {
  return bindingKey ? data?.find((ds) => ds.key === bindingKey)?.dataSource : undefined;
}

export function getNewApiCall(): DataBindingSourceInfo {
  return {
    sourceType: 'api-call',
    id: NEW_API_CALL,
    name: undefined,
    columns: [],
  };
}

export function getDataSourceId(dataSource: DataBindingSourceInfo): DataSourceId {
  return dataSource.sourceType === 'api-call'
    ? getApiCallSourceId(dataSource.id, dataSource.name)
    : dataSource.id;
}
